<script lang="ts">
import { computed, defineComponent, h, nextTick, ref, unref, watch } from 'vue';

import { classNames, formatDate } from '@components/utils';

import {
  makeDatetimeProps,
  makeDisabledProps,
  makeLocaleProps,
  makeReadonlyProps,
  makeThemeProps,
  provideTheme,
  useDatetime,
  useLocale,
  useRender
} from '../../composable';
import { CdsButton, CdsFunctionalButton } from '../CdsButton';
import { CdsTextInput } from '../CdsTextInput';
import { CdsTabsV2, CdsTabV2 } from '../CdsTabsV2';

import { findClosestExtremeNumber, makeTimeProps, useTime } from './composable';

import type { IRawDate, ComponentProps } from '@components/utils';

export const componentName = 'CdsTimePicker';
export default defineComponent({
  name: componentName,
  props: {
    ...makeTimeProps(),
    ...makeDatetimeProps({
      mask: 'HH:mm'
    }),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLocaleProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     * @type { any }
     */
    'update:modelValue': (value: string | undefined, date: IRawDate) => true
  },
  setup(props, { emit }) {
    const { tabindex, getLocale, getMask, getCurrentDate } = useDatetime(props);
    const { themeClasses } = provideTheme(props);
    const { time: timeLocale } = useLocale(props);
    const locale = ref(getLocale());
    const mask = computed(() => (props.withSeconds ? getMask() + ':ss' : getMask()));
    const {
      model,
      isAM,
      stringModel,
      hourInSelection,
      minuteInSelection,
      secondInSelection,
      validHours,
      validMinutes,
      validSeconds,
      getCurrentTime
    } = useTime(props, locale, mask, getCurrentDate);

    const attributes = computed(() => {
      if (props.disabled) return { 'aria-disabled': 'true' };
      if (props.readonly) return { 'aria-readonly': 'true' };
      return {};
    });
    const classes = computed(() => ({
      'cds-time-picker': true,
      'cds-time-picker--disabled': props.disabled,
      'cds-time-picker--readonly': props.readonly
    }));

    watch(
      () => props.modelValue,
      () => {
        if (props.format12h) {
          isAM.value = (unref(model).hour as number) < 12;
        }
      }
    );

    watch([mask, locale], () => {
      nextTick(() => {
        updateValue();
      });
    });

    function updateValue(date?: IRawDate) {
      date = Object.assign({ ...unref(model) }, date);

      const value = formatDate(
        new Date(
          date.year as number,
          date.month ? date.month - 1 : 0,
          date.day,
          date.hour,
          date.minute,
          date.second,
          date.millisecond
        ),
        unref(mask),
        unref(locale),
        date.year as number,
        date.timezoneOffset as number
      );

      date.changed = value !== props.modelValue;

      emit('update:modelValue', value, date);
    }

    function verifyAndUpdate() {
      // @todo ограничение по времени может работать некорректно в некоторых случаях, необходимо напиздячить тестов и проверить
      if (hourInSelection.value && !hourInSelection.value(model.value.hour as number)) {
        const allowedHours = findClosestExtremeNumber(validHours.value?.values as number[], model.value.hour as number);
        setHour(allowedHours);
        return;
      }

      if (minuteInSelection.value && !minuteInSelection.value(model.value.minute as number)) {
        const allowedMinutes = findClosestExtremeNumber(
          validMinutes.value?.values as number[],
          model.value.minute as number
        );
        setMinute(allowedMinutes);
        return;
      }

      if (props.withSeconds && unref(secondInSelection) && !unref(secondInSelection)!(unref(model).second as number)) {
        const allowedSeconds = findClosestExtremeNumber(
          unref(validSeconds)?.values as number[],
          unref(model).second as number
        );
        setSecond(allowedSeconds);
        return;
      }

      updateValue();
    }

    function setNow() {
      const date = {
        ...getCurrentDate(),
        ...getCurrentTime()
      };

      updateValue(date);
      Object.assign(unref(model), date);
    }

    function setHour(hour: number) {
      if (unref(model).hour !== hour) {
        model.value.hour = hour;
        verifyAndUpdate();
      }
    }

    function setMinute(minute: number) {
      if (unref(model).minute !== minute) {
        model.value.minute = minute;
        verifyAndUpdate();
      }
    }

    function setSecond(second: number) {
      if (unref(model).second !== second) {
        model.value.second = second;
        verifyAndUpdate();
      }
    }

    function setAm() {
      if (!unref(isAM)) {
        isAM.value = true;

        if (unref(model).hour !== null) {
          (model.value.hour as number) -= 12;
          verifyAndUpdate();
        }
      }
    }

    function setPm() {
      if (unref(isAM)) {
        isAM.value = false;

        if (unref(model).hour !== null) {
          (model.value.hour as number) += 12;
          verifyAndUpdate();
        }
      }
    }

    useRender(() => {
      const inputProps: ComponentProps<typeof CdsTextInput> & { class?: string; tabindex?: number | string } = {
        type: 'number',
        class: classNames('cds-time-picker__input'),
        placeholder: '00',
        hideMessages: true,
        readonly: props.readonly,
        disabled: props.disabled,
        tabindex: unref(tabindex)
      };

      const navButtonProps: ComponentProps<typeof CdsFunctionalButton> & {
        class?: string;
        tabindex?: number | string;
      } = {
        readonly: props.readonly,
        disabled: props.disabled,
        tabindex: -1
      };

      return h('div', { class: classNames(unref(classes), unref(themeClasses)), ...unref(attributes) }, [
        h('div', { class: classNames('cds-time-picker__container') }, [
          h('div', { class: 'cds-time-picker__clock' }, [
            h(CdsFunctionalButton, {
              ...navButtonProps,
              icon: 'chevron-up',
              onClick: () => setHour((unref(model).hour as number) + 1)
            }),
            h(CdsTextInput, {
              ...inputProps,
              value: unref(stringModel).hour,
              onInput: (event: InputEvent) => setHour(+(event.target as HTMLInputElement).value)
            }),
            h(CdsFunctionalButton, {
              ...navButtonProps,
              icon: 'chevron-down',
              onClick: () => setHour((unref(model).hour as number) - 1)
            })
          ]),
          h('div', { class: 'cds-time-picker__separator' }, ':'),
          h('div', { class: 'cds-time-picker__clock' }, [
            h(CdsFunctionalButton, {
              ...navButtonProps,
              icon: 'chevron-up',
              onClick: () => setMinute((unref(model).minute as number) + 1)
            }),
            h(CdsTextInput, {
              ...inputProps,
              value: unref(stringModel).minute,
              onInput: (event: InputEvent) => setMinute(+(event.target as HTMLInputElement).value)
            }),
            h(CdsFunctionalButton, {
              ...navButtonProps,
              icon: 'chevron-down',
              onClick: () => setMinute((unref(model).minute as number) - 1)
            })
          ]),
          props.withSeconds && h('div', { class: 'cds-time-picker__separator' }, ':'),
          props.withSeconds &&
            h('div', { class: 'cds-time-picker__clock' }, [
              h(CdsFunctionalButton, {
                ...navButtonProps,
                icon: 'chevron-up',
                onClick: () => setSecond((unref(model).second as number) + 1)
              }),
              h(CdsTextInput, {
                ...inputProps,
                value: unref(stringModel).second,
                onInput: (event: InputEvent) => setSecond(+(event.target as HTMLInputElement).value)
              }),
              h(CdsFunctionalButton, {
                ...navButtonProps,
                icon: 'chevron-down',
                onClick: () => setSecond((unref(model).second as number) - 1)
              })
            ]),
          props.format12h &&
            h(
              CdsTabsV2,
              { modelValue: unref(isAM) ? 'AM' : 'PM', grow: false, class: classNames('cds-time-picker__ampm') },
              {
                default: () => [
                  h(CdsTabV2, { value: 'AM', text: 'AM', onClick: setAm }),
                  h(CdsTabV2, { value: 'PM', text: 'PM', onClick: setPm })
                ]
              }
            )
        ]),
        props.nowBtn &&
          h('div', { class: 'cds-time-picker__now' }, [
            h(CdsButton, {
              appearance: 'tertiary',
              text: timeLocale.now,
              readonly: props.readonly,
              disabled: props.disabled,
              tabindex: unref(tabindex),
              onClick: setNow
            })
          ])
      ]);
    });

    return {
      setNow,
      setHour,
      setMinute,
      setSecond,
      setAm,
      setPm
    };
  }
});
</script>

<style lang="scss">
@import './CdsTimePicker';
</style>
